import { FunctionComponent, useContext } from 'react';
import { Box, FormControlLabel, Switch, Typography } from '@mui/material';
import { IMfaOrgUser } from '../../../shared/services/mfa/mfa.model';
import { MfaUserTable } from './mfa-user-table';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';

// Reusable enforcement control: the require-2FA toggle (with consequence-stating
// confirmations) plus the per-user method table. Shared by the org admin page and
// each client card on the partner page, so the enforcement copy lives in one
// place. The parent handlers do the API call, refresh and toast.
export const MfaEnforcementSection: FunctionComponent<{
  enforceMfa: boolean,
  users: IMfaOrgUser[],
  busy?: boolean,
  onToggleEnforcement: (next: boolean) => Promise<void> | void,
  onSetToEmail: (userId: string) => Promise<void> | void,
}> = ({ enforceMfa, users, busy, onToggleEnforcement, onSetToEmail }) => {
  const userNotifications = useContext(UserNotificationsContext);

  const handleToggle = async (next: boolean) => {
    const confirmed = next
      ? await userNotifications.openDialog({
        active: true,
        title: 'Require two-factor authentication?',
        message: 'Everyone without two-factor authentication will be switched to email codes now. Users can add an authenticator app, but will not be able to turn two-factor authentication off while this is on.',
        buttonCopy: 'Require it',
        cancelButtonCopy: 'Cancel',
      })
      : await userNotifications.openDialog({
        active: true,
        title: 'Stop requiring two-factor authentication?',
        message: 'This does not change anyone\'s current settings, but users will be able to turn two-factor authentication off themselves.',
        buttonCopy: 'Stop requiring',
        cancelButtonCopy: 'Cancel',
      });
    if (confirmed) await onToggleEnforcement(next);
  };

  const handleSetToEmail = async (userId: string) => {
    const confirmed = await userNotifications.openDialog({
      active: true,
      title: 'Set this user to email codes?',
      message: 'Their authenticator app will be removed and email verification codes turned on. They can set the app up again themselves.',
      buttonCopy: 'Set to email',
      cancelButtonCopy: 'Cancel',
    });
    if (confirmed) await onSetToEmail(userId);
  };

  return (
    <Box>
      <FormControlLabel
        control={<Switch checked={enforceMfa} disabled={busy} onChange={(e) => handleToggle(e.target.checked)} />}
        label="Require two-factor authentication for everyone"
      />
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, maxWidth: 640 }}>
        {enforceMfa
          ? 'Everyone keeps at least email verification codes. They can add an authenticator app on top, but cannot turn two-factor authentication off.'
          : 'When on, anyone without two-factor authentication is switched to email codes, and no one can turn it off.'}
      </Typography>
      <MfaUserTable users={users} onSetToEmail={handleSetToEmail} busy={busy} />
    </Box>
  );
};
